import { DetectionSuggestion } from './types';

export interface AggregatedSuggestion {
  detectedType: string;
  detectedBrand?: string;
  detectedColor?: string;
  totalQuantity: number;
  averageConfidence: number;
  requiresManualReview: boolean;
  suggestions: DetectionSuggestion[];
}

/**
 * Agreguje wyniki detekcji z wielu zdjęć
 * Grupuje sugestie według typu, marki i koloru, wybiera najczęstsze wartości
 */
export function aggregateDetectionResults(
  allSuggestions: DetectionSuggestion[][]
): AggregatedSuggestion | null {
  if (!allSuggestions || allSuggestions.length === 0) {
    return null;
  }

  // Spłaszcz wszystkie sugestie z wszystkich zdjęć
  const flatSuggestions = allSuggestions.flat();

  if (flatSuggestions.length === 0) {
    return null;
  }

  // Grupuj sugestie według klucza (type, brand, color)
  const groups = new Map<string, DetectionSuggestion[]>();

  for (const suggestion of flatSuggestions) {
    const key = `${suggestion.detectedType}|${suggestion.detectedBrand || 'unknown'}|${suggestion.detectedColor || 'unknown'}`;
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(suggestion);
  }

  // Znajdź grupę z najwyższą średnią confidence
  let bestGroup: DetectionSuggestion[] | null = null;
  let bestAvgConfidence = 0;

  for (const group of Array.from(groups.values())) {
    const avgConfidence = group.reduce((sum: number, s: DetectionSuggestion) => sum + s.confidence, 0) / group.length;
    if (avgConfidence > bestAvgConfidence) {
      bestAvgConfidence = avgConfidence;
      bestGroup = group;
    }
  }

  if (!bestGroup || bestGroup.length === 0) {
    return null;
  }

  // Wybierz najczęstsze wartości (mode)
  const typeCounts = new Map<string, number>();
  const brandCounts = new Map<string, number>();
  const colorCounts = new Map<string, number>();

  for (const suggestion of flatSuggestions) {
    typeCounts.set(suggestion.detectedType, (typeCounts.get(suggestion.detectedType) || 0) + 1);
    if (suggestion.detectedBrand) {
      brandCounts.set(suggestion.detectedBrand, (brandCounts.get(suggestion.detectedBrand) || 0) + 1);
    }
    if (suggestion.detectedColor) {
      colorCounts.set(suggestion.detectedColor, (colorCounts.get(suggestion.detectedColor) || 0) + 1);
    }
  }

  const mostCommonType = Array.from(typeCounts.entries())
    .sort((a, b) => b[1] - a[1])[0]?.[0] || bestGroup[0].detectedType;
  
  const mostCommonBrand = Array.from(brandCounts.entries())
    .sort((a, b) => b[1] - a[1])[0]?.[0];
  
  const mostCommonColor = Array.from(colorCounts.entries())
    .sort((a, b) => b[1] - a[1])[0]?.[0];

  // Oblicz całkowitą ilość i średnią confidence
  const totalQuantity = flatSuggestions.reduce((sum: number, s: DetectionSuggestion) => sum + s.quantity, 0);
  const avgConfidence = flatSuggestions.reduce((sum: number, s: DetectionSuggestion) => sum + s.confidence, 0) / flatSuggestions.length;
  const requiresManualReview = avgConfidence < 0.4 || bestGroup.some(s => s.requiresManualReview);

  return {
    detectedType: mostCommonType,
    detectedBrand: mostCommonBrand,
    detectedColor: mostCommonColor,
    totalQuantity,
    averageConfidence: Math.round(avgConfidence * 10000) / 10000, // Round to 4 decimal places
    requiresManualReview,
    suggestions: flatSuggestions,
  };
}

/**
 * Generuje opis produktu na podstawie zagregowanych wyników detekcji
 */
export function generateProductDescription(aggregated: AggregatedSuggestion): string {
  const parts: string[] = [];

  if (aggregated.detectedBrand) {
    parts.push(`Marka: ${aggregated.detectedBrand}`);
  }

  parts.push(`Typ: ${aggregated.detectedType}`);

  if (aggregated.detectedColor) {
    parts.push(`Kolor: ${aggregated.detectedColor}`);
  }

  parts.push(`Ilość: ${aggregated.totalQuantity} szt.`);

  const confidencePercent = Math.round(aggregated.averageConfidence * 100);
  parts.push(`Pewność rozpoznania: ${confidencePercent}%`);

  if (aggregated.requiresManualReview) {
    parts.push('(Wymaga ręcznej weryfikacji)');
  }

  return parts.join('. ') + '.';
}

